import { Tooltip } from "@mantine/core";
import { Link, useMatches } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { Soon } from "../../ui/Soon";
import { STUDIO_NAV, type StudioNavItem, studioSectionOf } from "./nav";
import classes from "./studio2.module.css";

/** The nav item of the page on screen, from the deepest matched route; `null` on Simple. */
export function useStudioSection() {
  return useMatches({
    select: (matches) => {
      const leaf = matches.at(-1);
      return leaf === undefined ? undefined : studioSectionOf(leaf.routeId);
    },
  });
}

// The nav marks the current item from the route (nav.ts), so a link is TanStack's active only on
// its exact location, and the two never disagree.
const EXACT = { exact: true };

interface ItemProps {
  item: StudioNavItem;
  /** With its label, or the compact rail's icon, named and with a tooltip on its right. */
  open: boolean;
  active: boolean;
  teamId: string;
  brandId: string;
  onNavigate: (() => void) | undefined;
}

function NavItem({ item, open, active, teamId, brandId, onNavigate }: ItemProps) {
  const Icon = item.icon;
  const body = (end?: ReactNode) =>
    open ? (
      <>
        <Icon size={18} strokeWidth={1.75} className={classes.navIcon} aria-hidden />
        <span>{item.label}</span>
        {item.badge === undefined ? end : <span className={classes.badge}>{item.badge}</span>}
      </>
    ) : (
      <Icon size={20} strokeWidth={1.5} className={classes.navIcon} aria-hidden />
    );
  // The rail's item, as the current shell's: its label its name and its tooltip, on its right.
  const compact = open ? {} : { "data-compact": true, "aria-label": item.label };
  switch (item.target.scope) {
    case "page": {
      const link = (
        <Link
          to={item.target.to}
          params={{ teamId, brandId }}
          activeOptions={EXACT}
          className={classes.navLink}
          data-active={active || undefined}
          aria-current={active ? "page" : undefined}
          onClick={onNavigate}
          {...compact}
        >
          {body()}
        </Link>
      );
      return open ? (
        link
      ) : (
        <Tooltip label={item.label} position="right">
          {link}
        </Tooltip>
      );
    }
    case "soon":
      if (!open) {
        // Not `disabled`: a disabled button gets no hover, so its tooltip would never show.
        return (
          <Tooltip label={`${item.label}: Coming soon`} position="right">
            <button type="button" aria-disabled="true" className={classes.navLink} {...compact}>
              {body()}
            </button>
          </Tooltip>
        );
      }
      return (
        <Soon block>
          {(props) => (
            <button type="button" className={classes.navLink} {...props}>
              {body()}
            </button>
          )}
        </Soon>
      );
  }
}

/** The prototype's nav: its groups and items in order, the current page's marked. */
export function StudioNav(props: {
  teamId: string;
  brandId: string;
  /** Labels, or the compact rail; the drawer's is always open. */
  open?: boolean;
  onNavigate?: () => void;
}) {
  const { teamId, brandId, open = true, onNavigate } = props;
  const section = useStudioSection();
  return (
    <>
      {STUDIO_NAV.map((group, index) => (
        <div key={group.title ?? `group-${index}`}>
          {/* The rail has no titles: a divider between groups, as the current shell's. */}
          {group.title === undefined || !open ? (
            index > 0 ? <div className={classes.divider} /> : null
          ) : (
            <p className={classes.groupTitle}>{group.title}</p>
          )}
          {group.items.map((item: StudioNavItem) => (
            <NavItem
              key={item.label}
              item={item}
              open={open}
              active={item.label === section}
              teamId={teamId}
              brandId={brandId}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      ))}
    </>
  );
}
